import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { TextArea } from "components/Form/FormikControls";
import { withNamespace } from "components/Form/FormikControls/lib";
import { Column } from "components/Layout";
import {
  ReviewCard,
  ReviewCardTitle,
  ReviewField,
  ReviewFieldGrid,
  SectionDivider,
} from "components/ReviewCard";

import {
  faBookDead,
  faCalendarDays,
  faCoins,
  faGavel,
  faHandshake,
  faSlidersH,
} from "@fortawesome/pro-duotone-svg-icons";

import { LivestockSalePricingTypeDisplayNameLkp } from "constants/pricingTypes";
import { getIconForSpeciesId } from "constants/species";

import {
  calculateTotalPriceCents,
  formatDollarsFromCents,
  formatWeightKg,
} from "lib";

import {
  getBusinesses,
  getCategories,
  getConsignmentById,
  getDentitionOptionsByAgeId,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getDressingRanges,
  getLabels,
  getProperties,
  getSaleSubTypeById,
  getSexes,
  getWeightRanges,
  selectActiveProducts,
  selectSpeciesAttributeOptionsBySpeciesId,
} from "selectors";

import {
  MobileTransactionalCreateSaleFormValues,
  SaleLotCreateFormValues,
} from "../lib/types";
import { useSaleLotAttributeLabels } from "../lib/useSaleLotAttributeLabels";
import { FormRow } from "./FormRow";
import { Accreditation } from "constants/draftingAttributes";

type SelectOption = { value: number | null; label: string };
type NamedEntity = { name: string };

interface SingleLotSummaryProps {
  saleLot: SaleLotCreateFormValues;
  speciesId: number | null;
  deploymentId: number | undefined;
  breedOptions: Array<SelectOption>;
  sexes: Record<number, { name: string; hasProgeny?: boolean }>;
  products: Record<string, NamedEntity>;
  businessByIdLookup: Record<string, NamedEntity>;
}

const SingleLotSummary = ({
  saleLot,
  speciesId,
  deploymentId,
  breedOptions,
  sexes,
  products,
  businessByIdLookup,
}: SingleLotSummaryProps) => {
  const labelsLookup = useSelector(getLabels);
  const dentitionOptions = useSelector(
    getDentitionOptionsByAgeId(saleLot.age_id),
  );
  const categories = useSelector(getCategories);
  const weightRanges = useSelector(getWeightRanges);
  const dressingRanges = useSelector(getDressingRanges);
  const speciesAttributeOptionsBySpeciesId = useSelector(
    selectSpeciesAttributeOptionsBySpeciesId,
  );

  const {
    pricing_type_id: pricingTypeId,
    product_id: productId,
    sex_id: sexId,
    invoiceToBusinessId,
    quantity,
    quantity_progeny: quantityProgeny,
    unit_price: unitPrice,
    total_mass_grams: totalMassGramsRaw,
    quantity_delivered: quantityDelivered,
    dentitionId,
    marks,
    labels,
    note,
    reservePrice,
    draftingAttributes,
    category_id: categoryId,
    exemption_id: exemptionId,
    estimatedAverageWeightId,
    dressingRangeId,
  } = saleLot;

  const totalMassGrams = Number(totalMassGramsRaw) || 0;
  // Create flow writes unitPrice (MultiSaleLotsForm); edit flow writes unit_price (PricingType.js)
  const unitPriceValue = saleLot.unitPrice ?? unitPrice ?? null;

  const dentitionLabel = dentitionOptions.find(
    d => d.value === dentitionId,
  )?.label;

  const categoryLabel = categoryId
    ? categories[categoryId]?.name || null
    : null;

  const weightRangeLabel = estimatedAverageWeightId
    ? weightRanges[estimatedAverageWeightId]?.shortCode || null
    : null;

  const dressingRange = dressingRangeId
    ? dressingRanges[dressingRangeId]
    : null;
  const dressingRangeLabel = dressingRange
    ? dressingRange.dressingRangeMaxPercent
      ? `${parseInt(dressingRange.dressingRangeMinPercent, 10)}-${parseInt(dressingRange.dressingRangeMaxPercent, 10)}%`
      : `${dressingRange.dressingRangeMinPercent}+%`
    : null;

  const exemptionOptions =
    speciesId != null
      ? speciesAttributeOptionsBySpeciesId[speciesId]?.exemptions ?? []
      : [];
  const exemptionLabel = exemptionId
    ? exemptionOptions.find(e => e.value === exemptionId)?.label || null
    : null;

  const invoiceToLabel = invoiceToBusinessId
    ? businessByIdLookup[invoiceToBusinessId]?.name || "-"
    : null;

  const selectedSexHasProgeny = Boolean(sexes[sexId]?.hasProgeny);

  const { ageLabel, breedLabel, sexLabel } = useSaleLotAttributeLabels(
    saleLot,
    deploymentId,
    speciesId,
  );
  const productLabel = productId ? products[productId]?.name || "-" : null;
  const pricingLabel =
    LivestockSalePricingTypeDisplayNameLkp[pricingTypeId] || "-";

  const markNames = marks?.map(mark => mark?.location).filter(Boolean) ?? [];
  const labelNames =
    labels?.map(id => labelsLookup[id]?.name || id).filter(Boolean) ?? [];

  return (
    <>
      {/* WHAT */}
      <ReviewCard>
        <ReviewCardTitle icon={getIconForSpeciesId(speciesId)}>
          What
        </ReviewCardTitle>

        {productLabel && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Description">
              {productLabel}
            </ReviewField>
          </ReviewFieldGrid>
        )}

        <ReviewFieldGrid>
          <ReviewField xs={6} label="Head">
            {quantity || "-"}
          </ReviewField>
          {sexLabel && (
            <ReviewField xs={6} label="Sex">
              {sexLabel}
            </ReviewField>
          )}
          {ageLabel && (
            <ReviewField xs={6} label="Age">
              {ageLabel}
            </ReviewField>
          )}
          {breedLabel && (
            <ReviewField xs={6} label="Breed">
              {breedLabel}
            </ReviewField>
          )}
          {selectedSexHasProgeny && quantityProgeny > 0 && (
            <ReviewField xs={6} label="Progeny">
              {quantityProgeny}
            </ReviewField>
          )}
          {dentitionLabel && (
            <ReviewField xs={6} label="Dentition">
              {dentitionLabel}
            </ReviewField>
          )}
        </ReviewFieldGrid>

        {markNames.length > 0 && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Marks">
              {markNames.join(", ")}
            </ReviewField>
          </ReviewFieldGrid>
        )}

        {labelNames.length > 0 && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Labels">
              {labelNames.join(", ")}
            </ReviewField>
          </ReviewFieldGrid>
        )}

        {draftingAttributes?.animalName && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Animal Name">
              {draftingAttributes.animalName}
            </ReviewField>
          </ReviewFieldGrid>
        )}

        {note && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Note">
              {note}
            </ReviewField>
          </ReviewFieldGrid>
        )}

        {(weightRangeLabel || dressingRangeLabel) && (
          <ReviewFieldGrid>
            {weightRangeLabel && (
              <ReviewField xs={6} label="Est Avg Weight">
                {weightRangeLabel}
              </ReviewField>
            )}
            {dressingRangeLabel && (
              <ReviewField xs={6} label="Est Dressing %">
                {dressingRangeLabel}
              </ReviewField>
            )}
          </ReviewFieldGrid>
        )}

        {categoryLabel && (
          <ReviewFieldGrid>
            <ReviewField xs={6} label="Category">
              {categoryLabel}
            </ReviewField>
          </ReviewFieldGrid>
        )}

        {exemptionLabel && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="EID Exemption">
              {exemptionLabel}
            </ReviewField>
          </ReviewFieldGrid>
        )}
      </ReviewCard>

      {/* ADVANCED DRAFTING */}
      {draftingAttributes &&
        (() => {
          const breeds = [1, 2, 3]
            .map(i => ({
              label: breedOptions.find(
                b => b.value === draftingAttributes[`advancedBreed${i}Id`],
              )?.label,
              hd: draftingAttributes[`advancedBreed${i}Hd`],
            }))
            .filter(b => b.label || b.hd);

          const hornEntries: [string, number][] = (
            [
              ["Polled", draftingAttributes.hornPolled],
              ["Horned", draftingAttributes.hornHorned],
              ["Dehorned", draftingAttributes.hornDehorned],
              ["Tipped", draftingAttributes.hornTipped],
              ["Scurred", draftingAttributes.hornScurred],
            ] as [string, number][]
          ).filter(([, v]) => v != null);

          const dentitionEntries: [string, number][] = (
            [
              ["Milk/Lamb", draftingAttributes.dentition0],
              ["2T", draftingAttributes.dentition2],
              ["4T", draftingAttributes.dentition4],
              ["6T", draftingAttributes.dentition6],
              ["8T", draftingAttributes.dentition8],
              ["Broken", draftingAttributes.dentitionBroken],
            ] as [string, number][]
          ).filter(([, v]) => v != null);

          const fatScoreEntries: [string, number][] = [1, 2, 3, 4, 5, 6]
            .map(
              i =>
                [`Score ${i}`, draftingAttributes[`fatScore${i}`]] as [
                  string,
                  number,
                ],
            )
            .filter(([, v]) => v != null);

          const muscleScoreEntries: [string, number][] = [1, 2, 3, 4, 5]
            .map(
              i =>
                [`Score ${i}`, draftingAttributes[`muscleScore${i}`]] as [
                  string,
                  number,
                ],
            )
            .filter(([, v]) => v != null);

          const accreditationFields = {
            accreditationEU: "EU",
            accreditationPTIC: "PTIC",
            accreditationPTE: "PTE",
            accreditationPCAS: "PCAS",
            accreditationAntibioticFree: "Antibiotic Free",
            accreditationOrganic: "Organic",
            accreditationGrassFed: "Grass Fed",
            stationMated: "Station Mated",
            notStationMated: "Not Station Mated",
            accreditationBackgrounded: "Backgrounded",
            feeder: "Feeder",
            animalHealthStatementOverride: "AHS",
            sixInOne: "6 in 1",
            drenched: "Drenched",
            ojdVac: "OJD Vac",
            gudairApproved: "Gudair Approved",
            b12Vac: "B12 Vac",
            backline: "Backline/Dipped",
            MAndTS: "M&TS",
            SAEligible: "SA Eligible",
            painRelief: "Pain Relief",
          } as const satisfies Record<string, string>;
          const setAccreditations = Object.entries(accreditationFields)
            .filter(
              ([key]) =>
                draftingAttributes[key] === Accreditation.ALL ||
                draftingAttributes[key] === true,
            )
            .map(([, label]) => label);

          const conditions = [
            draftingAttributes.conditionStore && "Store",
            draftingAttributes.conditionForward && "Forward",
            draftingAttributes.conditionPrime && "Prime",
          ].filter(Boolean) as string[];

          const hasAnyData =
            breeds.length > 0 ||
            hornEntries.length > 0 ||
            dentitionEntries.length > 0 ||
            fatScoreEntries.length > 0 ||
            muscleScoreEntries.length > 0 ||
            draftingAttributes.frameStructure ||
            draftingAttributes.accreditationJBAS ||
            setAccreditations.length > 0 ||
            conditions.length > 0 ||
            draftingAttributes.publicDescription ||
            draftingAttributes.publicFaultDescription ||
            draftingAttributes.partOfRun ||
            draftingAttributes.pregStatus ||
            draftingAttributes.joinedStart ||
            draftingAttributes.shearingStatus ||
            draftingAttributes.bloodline ||
            draftingAttributes.xBloodline ||
            draftingAttributes.vendorBredOverride ||
            draftingAttributes.ageRangeTimeUnit ||
            draftingAttributes.maxAge ||
            draftingAttributes.minAge ||
            draftingAttributes.tagNumber;

          if (!hasAnyData) return null;

          return (
            <ReviewCard>
              <ReviewCardTitle icon={faSlidersH}>
                Advanced Drafting
              </ReviewCardTitle>

              {breeds.map((b, i) => (
                <ReviewFieldGrid key={i}>
                  <ReviewField xs={8} label={`Breed ${i + 1}`}>
                    {b.label || "-"}
                  </ReviewField>
                  {b.hd != null && (
                    <ReviewField xs={4} label="Hd">
                      {b.hd}
                    </ReviewField>
                  )}
                </ReviewFieldGrid>
              ))}

              {(draftingAttributes.bloodline ||
                draftingAttributes.xBloodline ||
                draftingAttributes.bySire) && (
                <ReviewFieldGrid>
                  {draftingAttributes.bloodline && (
                    <ReviewField xs={4} label="Bloodline">
                      {draftingAttributes.bloodline}
                    </ReviewField>
                  )}
                  {draftingAttributes.xBloodline && (
                    <ReviewField xs={4} label="X-Bloodline">
                      {draftingAttributes.xBloodline}
                    </ReviewField>
                  )}
                  {draftingAttributes.bySire && (
                    <ReviewField xs={4} label="By - Sire">
                      {draftingAttributes.bySire}
                    </ReviewField>
                  )}
                </ReviewFieldGrid>
              )}

              {draftingAttributes.ageRangeTimeUnit &&
                (draftingAttributes.minAge || draftingAttributes.maxAge) && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Age Range">
                      {`${draftingAttributes.minAge} - ${draftingAttributes.maxAge} ${draftingAttributes.ageRangeTimeUnit}`}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}

              {hornEntries.length > 0 && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Horn (hd)">
                    {hornEntries.map(([l, v]) => `${l}: ${v}`).join(", ")}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {(draftingAttributes.isNotMouthed ||
                dentitionEntries.length > 0) && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Dentition (hd)">
                    {[
                      draftingAttributes.isNotMouthed ? "Not Mouthed" : null,
                      ...dentitionEntries.map(([l, v]) => `${l}: ${v}`),
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {fatScoreEntries.length > 0 && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Fat Score (hd)">
                    {fatScoreEntries.map(([l, v]) => `${l}: ${v}`).join(", ")}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {muscleScoreEntries.length > 0 && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Muscle Score (hd)">
                    {muscleScoreEntries
                      .map(([l, v]) => `${l}: ${v}`)
                      .join(", ")}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {draftingAttributes.frameStructure && (
                <ReviewFieldGrid>
                  <ReviewField xs={6} label="Frame Structure">
                    {draftingAttributes.frameStructure}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {(setAccreditations.length > 0 ||
                draftingAttributes.accreditationJBAS ||
                draftingAttributes.vendorBredOverride) && (
                <ReviewFieldGrid>
                  {setAccreditations.length > 0 && (
                    <ReviewField xs={12} label="Accreditations">
                      {setAccreditations.join(", ")}
                    </ReviewField>
                  )}
                  {!!draftingAttributes.accreditationJBAS && (
                    <ReviewField xs={6} label="JBAS">
                      {draftingAttributes.accreditationJBAS}
                    </ReviewField>
                  )}
                  {!!draftingAttributes.vendorBredOverride && (
                    <ReviewField xs={6} label="Vendor Bred">
                      {draftingAttributes.vendorBredOverride}
                    </ReviewField>
                  )}
                </ReviewFieldGrid>
              )}

              {conditions.length > 0 && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Condition">
                    {conditions.join(", ")}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {draftingAttributes.pregStatus && (
                <ReviewFieldGrid>
                  <ReviewField xs={6} label="Preg Status">
                    {draftingAttributes.pregStatus}
                  </ReviewField>
                  {draftingAttributes.sil != null && (
                    <ReviewField xs={6} label="SIL">
                      {draftingAttributes.sil}%
                    </ReviewField>
                  )}
                </ReviewFieldGrid>
              )}

              {(draftingAttributes.joinedStart ||
                draftingAttributes.joinedEnd) && (
                <ReviewFieldGrid>
                  {draftingAttributes.joinedStart && (
                    <ReviewField xs={6} label="Joined Start">
                      {draftingAttributes.joinedStart}
                    </ReviewField>
                  )}
                  {draftingAttributes.joinedEnd && (
                    <ReviewField xs={6} label="Joined End">
                      {draftingAttributes.joinedEnd}
                    </ReviewField>
                  )}
                </ReviewFieldGrid>
              )}

              {(draftingAttributes.shearingStatus ||
                draftingAttributes.shearing) && (
                <ReviewFieldGrid>
                  {draftingAttributes.shearingStatus && (
                    <ReviewField xs={6} label="Shearing Status">
                      {draftingAttributes.shearingStatus}
                    </ReviewField>
                  )}
                  {draftingAttributes.shearing && (
                    <ReviewField xs={6} label="Shearing Date">
                      {draftingAttributes.shearing}
                    </ReviewField>
                  )}
                </ReviewFieldGrid>
              )}

              {draftingAttributes.publicDescription && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Comments">
                    {draftingAttributes.publicDescription}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {draftingAttributes.publicFaultDescription && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Fault Description">
                    {draftingAttributes.publicFaultDescription}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {draftingAttributes.partOfRun && (
                <ReviewFieldGrid>
                  <ReviewField xs={12} label="Part of Run">
                    {draftingAttributes.partOfRun}
                  </ReviewField>
                </ReviewFieldGrid>
              )}

              {draftingAttributes.tagNumber && (
                <ReviewFieldGrid>
                  <ReviewField xs={6} label="Tag #">
                    {draftingAttributes.tagNumber}
                  </ReviewField>
                </ReviewFieldGrid>
              )}
            </ReviewCard>
          );
        })()}

      {/* PRICE */}
      <ReviewCard>
        <ReviewCardTitle icon={faCoins}>Price</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField xs={6} label="Pricing">
            {pricingLabel}
          </ReviewField>
          <ReviewField xs={6} label="Unit Price">
            {unitPriceValue || "-"}
          </ReviewField>
          {totalMassGrams > 0 && (
            <ReviewField xs={6} label="Total Mass">
              {formatWeightKg(totalMassGrams, true)}
            </ReviewField>
          )}
          {reservePrice && (
            <ReviewField xs={6} label="Reserve Price">
              {`${formatDollarsFromCents(reservePrice)}`}
            </ReviewField>
          )}
          {quantityDelivered && (
            <ReviewField xs={6} label="Hd Delivered">
              {quantityDelivered}
            </ReviewField>
          )}
        </ReviewFieldGrid>
        {invoiceToLabel && (
          <ReviewFieldGrid>
            <ReviewField xs={12} label="Invoice To">
              {invoiceToLabel}
            </ReviewField>
          </ReviewFieldGrid>
        )}
      </ReviewCard>
    </>
  );
};

interface ReviewTabProps {
  deploymentId: number | undefined;
  speciesId: number | null;
  saleNamespace?: string;
}

export const ReviewTab = ({
  deploymentId,
  speciesId,
  saleNamespace,
}: ReviewTabProps) => {
  const { values } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const businessByIdLookup = useSelector(getBusinesses);
  const propertiesLookup = useSelector(getProperties);
  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );
  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );
  const sexes = useSelector(getSexes);
  const products = useSelector(selectActiveProducts);

  const { saleLot, saleLots, skinsLots, sale } = values;

  // Vendor can come from consignment namespace (create flow) or be looked up
  // from the store via saleLot.consignment_id (edit lot flow).
  const consignmentFromStore = useSelector(
    getConsignmentById(saleLot.consignment_id),
  );
  const vendorId =
    values.consignment?.vendor_id ?? consignmentFromStore?.vendor_id ?? null;
  const vendorPropertyId =
    values.consignment?.vendor_property_id ??
    consignmentFromStore?.vendor_property_id ??
    null;
  const billingReference = values.consignment?.billing_reference ?? null;
  const date = sale?.date;
  const saleTitle = sale?.sale_title;
  const saleTypeName = sale?.sale_type ?? null;
  const saleSubTypeId = sale?.sale_sub_type_id ?? null;
  const saleSubType = useSelector(getSaleSubTypeById(saleSubTypeId));
  const saleSubTypeName = saleSubType?.name ?? null;

  const {
    buyer_id: buyerId,
    buyer_way: buyerWay,
    destination_property_id: destinationPropertyId,
  } = saleLot;

  const vendorLabel = vendorId
    ? businessByIdLookup[vendorId]?.name || "-"
    : "Not Set";
  const vendorPropertyLabel = vendorPropertyId
    ? propertiesLookup[vendorPropertyId]?.PIC || "-"
    : null;

  const buyerLabel = buyerId
    ? businessByIdLookup[buyerId]?.name || "-"
    : "Not Set";
  const buyerPropertyLabel = destinationPropertyId
    ? propertiesLookup[destinationPropertyId]?.PIC || "-"
    : null;
  const buyerWayLabel = buyerWay?.name || null;

  return (
    <>
      <Column gap={0} style={{ paddingTop: 8 }}>
        {saleNamespace && <SectionDivider>Sale</SectionDivider>}
        {/* WHEN - only in create-sale flow */}
        {saleNamespace && date && (
          <ReviewCard>
            <ReviewCardTitle icon={faCalendarDays}>When</ReviewCardTitle>
            <ReviewFieldGrid>
              <ReviewField xs={6} label="Sale Date">
                {date}
              </ReviewField>
            </ReviewFieldGrid>
          </ReviewCard>
        )}
        {/* Sale-level fields - only in create-sale flow */}
        {saleNamespace && (
          <ReviewCard>
            <ReviewCardTitle icon={faGavel}>Sale</ReviewCardTitle>
            <ReviewFieldGrid>
              <ReviewField xs={12} label="Sale Title">
                {saleTitle || "-"}
              </ReviewField>
              {saleTypeName && (
                <ReviewField xs={6} label="Sale Type">
                  {saleTypeName}
                </ReviewField>
              )}
              {saleSubTypeName && (
                <ReviewField xs={6} label="Sub Type">
                  {saleSubTypeName}
                </ReviewField>
              )}
            </ReviewFieldGrid>
            <FormRow>
              <Grid item xs={12}>
                <TextArea
                  name={withNamespace(saleNamespace, "notes")}
                  label="Sale Notes"
                  rows={2}
                />
              </Grid>
            </FormRow>
          </ReviewCard>
        )}

        {saleNamespace && (
          <SectionDivider>
            {saleLots?.length
              ? `${saleLots.length} ${saleLots.length === 1 ? "Lot" : "Lots"}`
              : "Lots"}
          </SectionDivider>
        )}

        {/* WHO */}
        <ReviewCard>
          <ReviewCardTitle icon={faHandshake}>Who</ReviewCardTitle>

          <ReviewFieldGrid>
            <ReviewField xs={6} label="Vendor">
              {vendorLabel}
            </ReviewField>
            {vendorPropertyLabel && (
              <ReviewField xs={6} label="Vendor PIC">
                {vendorPropertyLabel}
              </ReviewField>
            )}
          </ReviewFieldGrid>

          <ReviewFieldGrid>
            <ReviewField xs={6} label="Buyer">
              {buyerLabel}
            </ReviewField>
            {buyerPropertyLabel && (
              <ReviewField xs={6} label="Buyer PIC">
                {buyerPropertyLabel}
              </ReviewField>
            )}
          </ReviewFieldGrid>

          {!saleNamespace && buyerWayLabel && (
            <ReviewFieldGrid>
              <ReviewField xs={6} label="Buyer Way">
                {buyerWayLabel}
              </ReviewField>
            </ReviewFieldGrid>
          )}
        </ReviewCard>

        {/* WHAT — multi-lot (create flow) */}
        {saleLots && saleLots.length > 0
          ? saleLots.map((lot, i) => {
              const lotUnitPrice = lot.unitPrice ?? null;
              const lotTotalMassGrams = Number(lot.total_mass_grams) || 0;
              const lotProductLabel = lot.product_id
                ? products[lot.product_id]?.name || "-"
                : null;
              const lotSexLabel = lot.sex_id
                ? sexes[lot.sex_id]?.name || "-"
                : null;
              const lotAgeLabel = ageOptions.find(
                a => a.value === lot.age_id,
              )?.label;
              const lotBreedLabel = breedOptions.find(
                b => b.value === lot.breed_id,
              )?.label;
              const lotPricingLabel =
                LivestockSalePricingTypeDisplayNameLkp[lot.pricing_type_id] ||
                "-";

              return (
                <ReviewCard key={i}>
                  <ReviewCardTitle icon={getIconForSpeciesId(speciesId)}>
                    {saleLots.length > 1 ? `Lot ${i + 1}` : "Lot"}
                  </ReviewCardTitle>
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Head">
                      {lot.quantity || "-"}
                    </ReviewField>
                    {lotSexLabel && (
                      <ReviewField xs={6} label="Sex">
                        {lotSexLabel}
                      </ReviewField>
                    )}
                    {lotAgeLabel && (
                      <ReviewField xs={6} label="Age">
                        {lotAgeLabel}
                      </ReviewField>
                    )}
                    {lotBreedLabel && (
                      <ReviewField xs={6} label="Breed">
                        {lotBreedLabel}
                      </ReviewField>
                    )}
                    {lotProductLabel && (
                      <ReviewField xs={12} label="Description">
                        {lotProductLabel}
                      </ReviewField>
                    )}
                    {lotTotalMassGrams > 0 && (
                      <ReviewField xs={6} label="Total Mass">
                        {formatWeightKg(lotTotalMassGrams, true)}
                      </ReviewField>
                    )}
                  </ReviewFieldGrid>
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Pricing">
                      {lotPricingLabel}
                    </ReviewField>
                    <ReviewField xs={6} label="Unit Price">
                      {lotUnitPrice || "-"}
                    </ReviewField>
                  </ReviewFieldGrid>
                </ReviewCard>
              );
            })
          : null}

        {/* WHAT / Advanced Drafting / Price — single lot (edit flow) */}
        {!saleLots?.length && (
          <SingleLotSummary
            saleLot={saleLot}
            speciesId={speciesId}
            deploymentId={deploymentId}
            breedOptions={breedOptions}
            sexes={sexes}
            products={products}
            businessByIdLookup={businessByIdLookup}
          />
        )}

        {skinsLots?.some(lot => lot.quantity) && (
          <>
            <SectionDivider>
              {skinsLots.length > 1
                ? `${skinsLots.length} Skins Lots`
                : "Skins Lot"}
            </SectionDivider>
            {skinsLots.map(
              ({ quantity: skinQuantity, unitPrice: skinUnitPrice }, i) => (
                <ReviewCard key={i}>
                  <ReviewCardTitle icon={getIconForSpeciesId(speciesId)}>
                    {skinsLots.length > 1 ? `Skins Lot ${i + 1}` : "Skins Lot"}
                  </ReviewCardTitle>
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Qty">
                      {skinQuantity || "-"}
                    </ReviewField>
                    <ReviewField xs={6} label="Unit Price">
                      {skinUnitPrice ? `$${skinUnitPrice}` : "-"}
                    </ReviewField>
                  </ReviewFieldGrid>
                </ReviewCard>
              ),
            )}
            {(skinsLots.length > 0 || billingReference) && (
              <ReviewCard>
                <ReviewCardTitle icon={faBookDead}>Kill Sheet</ReviewCardTitle>
                {skinsLots.length > 0 && (
                  <ReviewFieldGrid>
                    <ReviewField xs={6} label="Total Skins Qty">
                      {skinsLots.reduce(
                        (sum, { quantity: lotQty }) => sum + (lotQty || 0),
                        0,
                      )}
                    </ReviewField>
                    <ReviewField xs={6} label="Total Skins Value">
                      {`$${(
                        skinsLots.reduce(
                          (sum, skinsLot) =>
                            sum + (calculateTotalPriceCents(skinsLot) || 0),
                          0,
                        ) / 100
                      ).toFixed(2)}`}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}
                {billingReference && (
                  <ReviewFieldGrid>
                    <ReviewField xs={12} label="Kill ID / Ref">
                      {billingReference}
                    </ReviewField>
                  </ReviewFieldGrid>
                )}
              </ReviewCard>
            )}
          </>
        )}
      </Column>
    </>
  );
};
